import moment from "moment"
import { get, post } from "helpers/api_helper"
import { formatPersonName } from "helpers/formatPersonName"
import {
  claimLeadPoolApi,
  deleteLeadApi,
  getAllLeadPagedUrl,
  getLeadOfficePagedUrl,
  getLeadPoolApi,
  normalizeLeadPagedResponse,
  setLeadArchivedApi,
} from "helpers/backend_helper"

export const agentId = () => parseInt(localStorage.getItem("userId"), 10)
export const brokerId = () => parseInt(localStorage.getItem("brokerId"), 10) || 0

const leadCacheHours = Number(process.env.REACT_APP_LEAD_CACHE_HOURS)
const leadCacheMs = (Number.isFinite(leadCacheHours) && leadCacheHours > 0 ? leadCacheHours : 1) * 60 * 60 * 1000

export const leadQueryOptions = {
  retry: false,
  refetchOnWindowFocus: false,
  staleTime: leadCacheMs,
  cacheTime: leadCacheMs,
}

export const PERIODS = [
  { value: "3", label: "Last 3 months" },
  { value: "6", label: "Last 6 months" },
  { value: "9", label: "Last 12 months" },
  { value: "12", label: "This year" },
  { value: "0", label: "All time" },
]

export const periodLabel = value => (PERIODS.find(item => item.value === value) || PERIODS[0]).label

let localizationCache = null
const localization = () => {
  if (!localizationCache) {
    try {
      localizationCache = JSON.parse(localStorage.getItem("localData")) || {}
    } catch (error) {
      localizationCache = {}
    }
  }
  return localizationCache
}

export const sourceLabel = lead => (lead?.leadSourceId ? localization()[`LeadSource_${lead.leadSourceId}`] || "Other" : "Source missing")

export const statusLabel = lead => (lead?.leadStatusId > 1 ? localization()[`LeadStatus_${lead.leadStatusId}`] || "" : "")

export const statusTone = label => {
  const text = String(label || "").toLowerCase()
  if (text.includes("hot")) return "hot"
  if (text.includes("warm")) return "warm"
  if (text.includes("cold")) return "cold"
  return label ? "other" : "none"
}

const STATUS_RANK = { hot: 0, warm: 1, cold: 2, other: 3, none: 4 }
export const statusRank = lead => STATUS_RANK[statusTone(statusLabel(lead))]

export const leadName = lead => formatPersonName(lead?.firstName, lead?.lastName) || "No name"

export const leadAgentName = lead =>
  formatPersonName(lead?.agentFirstName, lead?.agentLastName) ||
  formatPersonName(lead?.agentName) ||
  lead?.agentFullName ||
  lead?.assignedAgentName ||
  lead?.brokerAgentName ||
  (lead?.agentId ? `Agent ${lead.agentId}` : "—")

export const leadLink = lead => `/lead/lead-detail/${lead.id}/${lead.leadId}`

export const validDate = value => {
  if (!value) return null
  const date = moment(value)
  return date.isValid() && date.year() > 1900 ? date : null
}

export const shortDate = value => {
  const date = validDate(value)
  return date ? date.format("D MMM YYYY") : "—"
}

export const dayMonth = value => {
  const date = validDate(value)
  return date ? date.format("D MMM") : "—"
}

export const daysSince = value => {
  const date = validDate(value)
  return date ? Math.max(0, moment().startOf("day").diff(date.clone().startOf("day"), "days")) : null
}

export const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`

export const readiness = lead => `${Math.round(Number(lead?.leadQualificationPoints) || 0)}%`

export const contactLine = lead => [lead?.email, lead?.mobile].filter(Boolean).join(" · ")

export const matchesQuery = (lead, query) =>
  !query ||
  [leadName(lead), leadAgentName(lead), lead.firstName, lead.lastName, lead.mobile, lead.email]
    .map(value => String(value || "").toLowerCase())
    .some(value => value.includes(query))

export const isNeverContacted = lead => !validDate(lead?.lastContact)

const pagedBody = ({ collectionId = 0, pageNumber = 1, pageSize = 10, archived = false }) => ({
  brokerId: agentId(),
  collectionId: Number(collectionId) || 0,
  pageNumber,
  pageSize,
  sortBy: "createdDate",
  sortDirection: "desc",
  filterColumns: [],
  filterValues: [],
  IsArchived: archived,
})

const agentPagedBody = ({ filterAgentId, collectionId = 0, pageNumber = 1, pageSize = 10, archived = false }) => ({
  agentId: Number(filterAgentId),
  collectionId: Number(collectionId) || 0,
  pageNumber,
  pageSize,
  sortBy: "createdDate",
  sortDirection: "desc",
  filterColumns: [],
  filterValues: [],
  isTransffered: -1,
  IsArchived: archived,
})

export const fetchLeadPage = async options =>
  normalizeLeadPagedResponse(
    Number(options.filterAgentId) ? await getLeadOfficePagedUrl(agentPagedBody(options)) : await getAllLeadPagedUrl(pagedBody(options))
  )

export const fetchAllLeads = async ({ collectionId = 0, archived = false, filterAgentId = 0 }) => {
  const page = await fetchLeadPage({
    collectionId,
    archived,
    filterAgentId,
    pageNumber: 1,
    pageSize: 500,
  })
  if (page.totalCount <= page.items.length) return page.items
  const all = await fetchLeadPage({
    collectionId,
    archived,
    filterAgentId,
    pageNumber: 1,
    pageSize: Math.max(page.totalCount, 1),
  })
  return all.items
}

export const fetchOfficeAgents = async () => {
  const response = await get("AgentExt/AllAgentByOffice", { params: { clientId: agentId() } })
  return (Array.isArray(response) ? response : [])
    .map(agent => ({ id: agent.id, name: formatPersonName(agent.firstName, agent.lastName) || formatPersonName(agent.agentName) || "No name" }))
    .filter(agent => agent.id)
    .sort((a, b) => a.name.localeCompare(b.name))
}

const listOf = response => {
  if (Array.isArray(response)) return response
  if (!response || typeof response !== "object") return []
  return response.resultData || response.items || response.data || response.Data || response.value || []
}

export const fetchPoolLeads = async () => {
  const response = await getLeadPoolApi({ brokerId: brokerId(), isActive: true })
  return listOf(response).map(item => {
    const nested = item?.lead && typeof item.lead === "object" ? item.lead : null
    return {
      ...item,
      id: item?.id ?? nested?.id,
      firstName: item?.firstName ?? nested?.firstName ?? "",
      lastName: item?.lastName ?? nested?.lastName ?? "",
      email: item?.email ?? nested?.email ?? "",
      mobile: item?.mobile ?? item?.phone ?? nested?.mobile ?? nested?.phone ?? "",
      submittedDate: item?.submittedDate ?? item?.createdDate ?? item?.addedDate ?? nested?.submittedDate,
    }
  })
}

export const claimPoolLead = leadId => claimLeadPoolApi({ leadId, brokerId: brokerId() })

export const archiveLead = (lead, archived = true) => setLeadArchivedApi({ id: lead.id, agentId: agentId(), isArchived: archived })

export const deleteLeadsForever = ids => (ids.length === 1 ? deleteLeadApi(ids[0]) : post("Lead/bulkDeleteLeads", ids))

export const fetchLeadCounts = async period => {
  const response = await get("Lead/getLeadCountDashboardByAgentMonth", {
    params: { agentId: agentId(), year: new Date().getFullYear(), months: period },
  })
  const rows = Array.isArray(response) ? response : []
  const pick = name => Number(rows.find(item => item.sources === name)?.counts) || 0
  const total = rows.find(item => item.sources === "Total") || {}
  const generated = Number(total.counts) || 0
  const converted = Number(total.counts2) || 0
  return {
    generated,
    converted,
    active: Number(total.counts1) || 0,
    conversion: generated > 0 ? Math.round((converted / generated) * 10000) / 100 : 0,
    hot: pick("Hot"),
    warm: pick("Warm"),
    cold: pick("Cold"),
    notSet: pick("Not Set"),
  }
}

export const fetchLeadSources = async period => {
  const response = await get(
    `Lead/getLeadSourceDashboardByAgentMonths?agentId=${agentId()}&year=${new Date().getFullYear()}&months=${encodeURIComponent(period)}`
  )
  return (Array.isArray(response) ? response : [])
    .map(item => ({ label: item.sources || "Source missing", count: Number(item.counts) || 0 }))
    .filter(item => item.count > 0)
    .sort((a, b) => b.count - a.count)
}

export const fetchLeadsPerMonth = async period => {
  const response = await get(`Lead/getLeadDashboardMonthByAgentMonths?agentId=${agentId()}&months=${encodeURIComponent(period)}`)
  const months = response?.months || []
  const counts = response?.counts || []
  return months.map((month, index) => ({ label: String(month), count: Number(counts[index]) || 0 }))
}

export const fetchLeadActivities = async days => {
  const response = await post("LeadActivity/allfilteredleadactivitiesbydate", {
    activityTypeId: [0],
    agentId: agentId(),
    category: 3,
    days,
  })
  return Array.isArray(response) ? response : []
}
